import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Alert, Checkbox,
    TextField,
    InputAdornment,
    Card
} from "@mui/material";
import { CheckCircle, PendingActions, DoneAll, Search } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import dayjs from "dayjs";
import PaymentApi from "Service/Payment.api";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { formatDate } from "Utils/Common";
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { StyledTableCell, StyledTableRow, HeaderCard, SearchCard, StyledContainer } from "./ProfessionalUserList";
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { Grid, Fade, Slide, CardContent, Divider, Tooltip, IconButton } from "@mui/material";


const SuperAdminApproval: React.FC = () => {
    const navigate = useNavigate();
    const [pendingList, setPendingList] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]); // Track selections
    const [completedList, setCompletedList] = useState<any[]>([]);
    const [searchPendingQuery, setSearchPendingQuery] = useState("");
    const [searchCompletedQuery, setSearchCompletedQuery] = useState("");
    const dispatch = useDispatch();

    useEffect(() => {
        fetchPending();
        fetchCompleted();
    }, []);

    const fetchPending = async () => {
        dispatch(startLoading({ loading: true, loadtext: "Loading pending approvals..." }));
        try {
            const res = await PaymentApi.getPendingPayouts();
            setPendingList(res.data || []);
            setSelectedIds([]); // Reset selection on refresh
        } finally {
            dispatch(stopLoading());
        }
    };

    const fetchCompleted = async () => {
        dispatch(startLoading({ loading: true, loadtext: "Loading payouts..." }));
        try {
            const res = await PaymentApi.getCompletedPayouts();
            setCompletedList(res.data || []);
        } finally {
            dispatch(stopLoading());
        }
    };


    // Toggle single selection
    const handleSelect = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    // Toggle Select All
    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {

        if (event.target.checked) {
            const allIds = pendingList.map(row => row.pkPayout);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    };

    // Unified Approve Function (Works for single and multiple)
    const processApproval = async (ids: number[]) => {
        const message = ids.length > 1
            ? `Confirm approval for ${ids.length} payouts?`
            : "Confirm this payout?";

        // if (!window.confirm(message)) return;

        dispatch(startLoading({ loading: true, loadtext: "Processing Approval..." }));
        try {
            // Assuming your API handles an array of IDs
            // If it only handles one, you might need a Promise.all or a different endpoint
            await PaymentApi.approvePayoutsBulk(ids);

        } catch (error) {
            // alert("Approval failed.");
        } finally {
            dispatch(stopLoading());
            dispatch(
                setAlertData({
                    alertMessage: "Payouts Approved Successfully",
                    alertVariant: "success",
                    openAlert: true,
                })
            );
            fetchPending();
            fetchCompleted();
        }
    };

    // 3. OPTIMIZED SEARCH: Pending Table
    const filteredPending = useMemo(() => {
        const query = searchPendingQuery.toLowerCase().trim();
        if (!query) return pendingList;
        return pendingList.filter(item =>
            item.professional?.toLowerCase().includes(query) ||
            item.caseTitle?.toLowerCase().includes(query) ||
            item.caseId?.toString().includes(query) ||
            item.amount?.toString().includes(query) ||
            formatDate(item.paymentDate).toLowerCase().includes(query)
        );
    }, [searchPendingQuery, pendingList]);

    // 4. OPTIMIZED SEARCH: Completed Table
    const filteredCompleted = useMemo(() => {
        const query = searchCompletedQuery.toLowerCase().trim();
        if (!query) return completedList;
        return completedList.filter(item =>
            item.professional?.toLowerCase().includes(query) ||
            item.caseTitle?.toLowerCase().includes(query) ||
            item.caseId?.toString().includes(query) ||
            item.amount?.toString().includes(query) ||
            formatDate(item.paymentDate).toLowerCase().includes(query) ||
            formatDate(item.completionDate).toLowerCase().includes(query)
        );
    }, [searchCompletedQuery, completedList]);

    const handleDownload = async () => {
        dispatch(startLoading({ loading: true, loadtext: "Generating Report..." }));
        try {
            const response = await PaymentApi.downloadCompletedPayouts();

            const blobData = response.data ? response.data : response;

            const blob = new Blob([blobData], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', `Completed_Payouts_${dayjs().format('YYYY-MM-DD')}.xlsx`);

            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
        } finally {
            dispatch(stopLoading());
        }
    };

    // Calculate the sum of selected payouts in real-time
const totalSelectedValue = useMemo(() => {
    return pendingList
        .filter(item => selectedIds.includes(item.pkPayout))
        .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
}, [selectedIds, pendingList]);

    return (
        <StyledContainer maxWidth="xl">
            <Fade in timeout={800}>
                <Box sx={{ width: '100%', pb: 10 }}>
                    {/* Back Button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                            sx={{ fontWeight: 'bold' }}
                        >
                            Back
                        </Button>
                    </Box>

                    {/* Header Section */}
                    <HeaderCard elevation={8} sx={{ mb: 4 }}>
                        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                flexDirection={{ xs: 'column', md: 'row' }}
                                gap={2}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <PaymentsIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56 } }} />
                                    <Box>
                                        <Typography
                                            variant="h4"
                                            component="h1"
                                            fontWeight="bold"
                                            color="white"
                                            gutterBottom
                                        >
                                            Payout Approval Queue
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ opacity: 0.9 }}
                                            color="white"
                                        >
                                            Review and finalize payout amounts entered by Admins.
                                        </Typography>
                                    </Box>
                                </Box>

                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<DoneAll />}
                                    disabled={selectedIds.length === 0}
                                    onClick={() => processApproval(selectedIds)}
                                    sx={{
                                        borderRadius: 2,
                                        px: 4,
                                        height: 50,
                                        width: { xs: '100%', md: 'auto' },
                                        bgcolor: 'white',
                                        color: '#283165',
                                        '&:hover': { bgcolor: '#f0f0f0' },
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Approve Selected ({selectedIds.length})
                                </Button>
                            </Box>
                        </CardContent>
                    </HeaderCard>

                    <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
                        You can approve payouts individually or use the checkboxes to approve in bulk.
                    </Alert>

                    {/* Pending Payouts Section */}
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PendingActions /> Pending Approvals
                    </Typography>

                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <SearchCard elevation={2}>
                                <CardContent sx={{ p: '16px !important' }}>
                                    <TextField
                                        fullWidth
                                        value={searchPendingQuery}
                                        onChange={(e) => setSearchPendingQuery(e.target.value)}
                                        placeholder="Search by professional, case or amount..."
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon color="primary" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />
                                </CardContent>
                            </SearchCard>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 3,
                                bgcolor: selectedIds.length > 0 ? '#f0fdf4' : 'white',
                                border: selectedIds.length > 0 ? '1px solid #16a34a' : '1px solid #e2e8f0',
                                transition: 'all 0.3s ease'
                            }}>
                                <Box textAlign="center" p={2}>
                                    <Typography variant="caption" fontWeight="bold" color="textSecondary" display="block">
                                        TOTAL SELECTED VALUE
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold" color={selectedIds.length > 0 ? "success.main" : "text.disabled"}>
                                        ₹{totalSelectedValue.toLocaleString('en-IN')}
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>

                    <Slide direction="up" in timeout={600}>
                        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 4, maxHeight: 500, overflow: 'auto', mb: 6 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={selectedIds.length > 0 && selectedIds.length < pendingList.length}
                                                checked={pendingList.length > 0 && selectedIds.length === pendingList.length}
                                                onChange={handleSelectAll}
                                                sx={{ color: "white", '&.Mui-checked': { color: "white" } }}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell>Professional</StyledTableCell>
                                        <StyledTableCell>Case Details</StyledTableCell>
                                        <StyledTableCell>Amount</StyledTableCell>
                                        <StyledTableCell>Date</StyledTableCell>
                                        <StyledTableCell align="center">Action</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredPending.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                                <Typography variant="body1" color="textSecondary">No pending payouts found.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredPending.map((row) => (
                                            <StyledTableRow key={row.pkPayout} selected={selectedIds.includes(row.pkPayout)}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedIds.includes(row.pkPayout)}
                                                        onChange={() => handleSelect(row.pkPayout)}
                                                        color="primary"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{row.professional}</TableCell>
                                                <TableCell>
                                                    <Typography variant="subtitle2">{row.caseTitle}</Typography>
                                                    <Typography variant="caption" color="textSecondary">ID: {row.caseId}</Typography>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#283165' }}>₹{row.amount}</TableCell>
                                                <TableCell>{dayjs(row.paymentDate).format("DD MMM YYYY")}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        startIcon={<CheckCircle />}
                                                        onClick={() => processApproval([row.pkPayout])}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        Approve
                                                    </Button>
                                                </TableCell>
                                            </StyledTableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Slide>

                    <Divider sx={{ my: 4 }} />

                    {/* Completed Payouts Section */}
                    <Box sx={{ mt: 4 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountBalanceWalletIcon /> Completed Payouts
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownload}
                                sx={{ borderRadius: 2 }}
                            >
                                Download Excel Report
                            </Button>
                        </Box>

                        <SearchCard elevation={1} sx={{ mb: 3 }}>
                            <CardContent sx={{ p: '12px !important' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={searchCompletedQuery}
                                    onChange={(e) => setSearchCompletedQuery(e.target.value)}
                                    placeholder="Search payout history..."
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon color="primary" />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </CardContent>
                        </SearchCard>

                        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2, maxHeight: 400, overflow: 'auto' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Professional</StyledTableCell>
                                        <StyledTableCell>Case Details</StyledTableCell>
                                        <StyledTableCell>Amount</StyledTableCell>
                                        <StyledTableCell>Submitted</StyledTableCell>
                                        <StyledTableCell>Completed</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredCompleted.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                                <Typography variant="body2" color="textSecondary">No payout history available.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredCompleted.map((row) => (
                                            <StyledTableRow key={row.pkPayment}>
                                                <TableCell>{row.professional}</TableCell>
                                                <TableCell>
                                                    <Typography variant="subtitle2">{row.caseTitle}</Typography>
                                                    <Typography variant="caption" color="textSecondary">ID: {row.caseId}</Typography>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#16a34a' }}>₹{row.amount}</TableCell>
                                                <TableCell>{dayjs(row.paymentDate).format("DD MMM YYYY")}</TableCell>
                                                <TableCell>{dayjs(row.completionDate).format("DD MMM YYYY")}</TableCell>
                                            </StyledTableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>
            </Fade>
        </StyledContainer>
    );
};

export default SuperAdminApproval;